import { FC } from 'react';
import { Button } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { useAtomValue, useSetAtom } from 'jotai';
import type { FormationManagementFacets } from 'types/api-responses';
import {
  closeGeologicIntervalDrawerAtom,
  geologicIntervalDrawerStateAtom,
} from './atoms';
import { AddGeologicIntervalContent } from './add-geologic-interval-content';
import { EditGeologicIntervalContent } from './edit-geologic-interval-content';
import styles from './geologic-interval-drawer.module.scss';

export interface GeologicIntervalDrawerProps {
  facets: FormationManagementFacets | undefined;
  // Fired after a successful create / update so the list page can refetch.
  onSaved: () => void;
}

export const GeologicIntervalDrawer: FC<GeologicIntervalDrawerProps> = ({
  facets,
  onSaved,
}) => {
  const state = useAtomValue(geologicIntervalDrawerStateAtom);
  const close = useSetAtom(closeGeologicIntervalDrawerAtom);

  if (state.mode === null) return null;

  return (
    <aside
      className={styles.aside}
      role="dialog"
      aria-label={
        state.mode === 'add'
          ? 'Add Geologic Interval'
          : 'Edit Geologic Interval'
      }
    >
      <div className={styles.header}>
        <div className={styles.headerText}>
          <p className={styles.eyebrow}>
            {state.mode === 'add'
              ? 'NEW'
              : (state.target?.basinName.toUpperCase() ?? 'EDIT')}
          </p>
          <h2 className={styles.title}>
            {state.mode === 'add'
              ? 'Geologic Interval'
              : (state.target?.geologicInterval ?? 'Geologic Interval')}
          </h2>
        </div>
        <Button
          hasIconOnly
          size="md"
          kind="ghost"
          onClick={close}
          iconDescription="Close"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={(props) => <Close size={20} {...props} />}
        />
      </div>
      {state.mode === 'add' && (
        <AddGeologicIntervalContent facets={facets} onCreated={onSaved} />
      )}
      {state.mode === 'edit' && state.target && (
        <EditGeologicIntervalContent
          key={state.target.id}
          target={state.target}
          onUpdated={onSaved}
        />
      )}
    </aside>
  );
};
